import { Loader2 } from "lucide-react";
import { useState } from "react";
import { ApiError } from "@/api";
import type { OperationGate } from "@/components/test-cases/prodCase";
import type { TestCaseSource } from "@/components/test-cases/testCasesSource";
import {
  Badge,
  Banner,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Skeleton,
} from "@/components/ui";
import type {
  CriteriaRegenerationBody,
  CriteriaVersion,
  CriteriaVersionList,
  TestCaseCriteria,
  TestCaseDetail,
} from "@/types";
import { compactTime } from "@/utils/format";

/** Said with every failure: a failed attempt never touches the criteria in use. */
export const KEEP_CRITERIA_TEXT = "기존 기준을 그대로 씁니다.";

/**
 * The failure codes of `POST …/criteria-regenerations` (TCFEEDBACK B8), in
 * words for the operator. The two that ask for consent are not here — they
 * open a dialog instead of failing.
 */
const REGEN_ERROR_TEXT: Record<string, string> = {
  CRITERIA_CHANGED_MEANWHILE: "그사이 기준이나 👎가 바뀌었습니다. 새로 고친 뒤 다시 시도해 주세요.",
  FEEDBACK_CHANGED_MEANWHILE: "그사이 기준이나 👎가 바뀌었습니다. 새로 고친 뒤 다시 시도해 주세요.",
  FEEDBACK_UPDATE_ORDER_UNDEFINED:
    "같은 👎가 여러 번 고쳐졌는데 어느 것이 마지막인지 알 수 없어 새 기준을 만들 수 없습니다.",
  TOO_MANY_FEEDBACKS: "👎가 너무 많아 코멘트를 줄여도 한 번에 넣을 수 없어 새 기준을 만들 수 없습니다.",
  NO_FEEDBACK_COPY: "이 답의 👎를 받아 둔 기록이 없어 새 기준을 만들 수 없습니다.",
  CRITERIA_GENERATION_FAILED: "새 기준을 만드는 중에 실패했습니다. 잠시 뒤 다시 시도해 주세요.",
  CRITERIA_UNSCOREABLE: "만들어진 기준으로는 채점할 수 없어 저장하지 않았습니다.",
  CRITERIA_SAVE_FAILED: "새 기준을 저장하지 못했습니다.",
  CRITERIA_REGEN_NOT_STARTED: "새 기준 만들기를 시작하지 못했습니다.",
  // Only reached when the server refuses again after the person agreed.
  SCORING_CHANGE_NEEDS_CONFIRM: "채점 방식 변경에 동의했지만 서버가 받아 주지 않았습니다.",
};

/**
 * Whether a failed POST says the criteria stayed as they were (VERIFY-86 #2).
 *
 * A coded answer from the server is definite: refused, not generated, not saved,
 * not started — nothing changed. A request that never left (status 0) changed
 * nothing either. A timeout, a lost connection, a gateway timeout or an uncoded
 * 5xx, and the server's own CRITERIA_REGEN_RESULT_UNKNOWN, are not: the new
 * version may exist, so the screen checks instead of saying "kept".
 */
export function regenerationOutcomeUnknown(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  if (err.code === "CRITERIA_REGEN_RESULT_UNKNOWN") return true;
  if (err.status === 504) return true;
  // The host's catch-all for an unhandled exception says nothing about the save.
  if (err.code === "INTERNAL_ERROR") return true;
  return err.status >= 500 && !err.code;
}

export function regenerationErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code && Object.prototype.hasOwnProperty.call(REGEN_ERROR_TEXT, err.code)) {
      return REGEN_ERROR_TEXT[err.code];
    }
    if (err.status === 403) return "이 TC의 기준을 다시 만들 권한이 없습니다.";
    if (err.status === 404) return "이 서버는 아직 기준 다시 만들기를 지원하지 않거나 TC를 찾지 못했습니다.";
  }
  return `새 기준을 만들지 못했습니다 · ${err instanceof Error ? err.message : String(err)}`;
}

type Pending = { kind: "start" } | { kind: "scoring"; flags: Flags };

type Flags = Pick<CriteriaRegenerationBody, "confirm_scoring_change">;

/** How the 👎 comments reached the generator (user decision C2). */
export function commentModeText(mode: string | null | undefined): string {
  if (mode === "summarized") return "코멘트가 길어 AI로 요약해서 넣음";
  if (mode === "truncated") return "코멘트가 길어 일부 잘라서 넣음 (잘림)";
  return "";
}

const SCORING_CHANGE_TEXT = "채점 방식이 바뀌고 이전 점수와 비교할 수 없습니다.";

/**
 * The 확정 채점 기준 block: the criteria in use, their version, and the one
 * way to remake them — a person's click, never an automatic run.
 *
 * Keyed by TC id in the drawer, so a half-finished flow never carries over
 * to another TC.
 */
export function CriteriaBlock({
  detail,
  source,
  gate,
  onRegenerated,
}: {
  detail: TestCaseDetail;
  source: TestCaseSource;
  gate: OperationGate | null;
  /** Refetch the detail without blanking the drawer; false (or a rejection) when it could not. */
  onRegenerated: () => Promise<boolean | void>;
}) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState("");
  const [failure, setFailure] = useState("");
  // Saved, but the detail could not be read again: only the read is retried.
  const [rereadFailed, setRereadFailed] = useState(false);
  // The POST's outcome is not known: the screen reads again, it never resends.
  const [unknown, setUnknown] = useState<{ operationId: string | null } | null>(null);
  const [checking, setChecking] = useState(false);

  const reread = async (): Promise<boolean> => {
    setChecking(true);
    try {
      return (await onRegenerated()) !== false;
    } catch {
      return false;
    } finally {
      setChecking(false);
    }
  };

  const checks = detail.criteria?.checks ?? null;
  // Known only from a BE that sends the hash with the detail; else the server asks.
  const noCriteria = detail.criteria == null && detail.criteria_sha256 !== undefined;
  const count = detail.answer_feedbacks?.length;
  const allowed = gate?.allowed ?? false;

  const send = async (flags: Flags) => {
    setPending(null);
    setRunning(true);
    setNotice("");
    setFailure("");
    setRereadFailed(false);
    setUnknown(null);
    let result;
    try {
      result = await source.regenerateCriteria(detail.test_case_id, detail.source_environment, {
        expected_version: detail.criteria_version ?? null,
        expected_criteria_sha256: detail.criteria_sha256 ?? null,
        ...flags,
      });
    } catch (err) {
      // The consent comes back as 409 before any model call; asking is the
      // next step, not a failure (the start dialog already asks when it can tell).
      if (err instanceof ApiError && err.code === "SCORING_CHANGE_NEEDS_CONFIRM" && !flags.confirm_scoring_change) {
        setPending({ kind: "scoring", flags });
      } else if (regenerationOutcomeUnknown(err)) {
        const operationId = err instanceof ApiError && typeof err.detail?.operation_id === "string"
          ? err.detail.operation_id : null;
        setUnknown({ operationId });
        setRunning(false);
        await reread();
        return;
      } else {
        setFailure(regenerationErrorText(err));
      }
      setRunning(false);
      return;
    }
    // Saved: that stands whatever the next read does.
    const mode = commentModeText(result.comment_mode);
    setNotice(`기준 v${result.version}을 만들었습니다.${mode ? ` ${mode}.` : ""}`);
    setRunning(false);
    if (!(await reread())) setRereadFailed(true);
  };

  const accept = () => {
    if (!pending) return;
    // C1: a TC without criteria changes how it is scored; the start dialog said so.
    if (pending.kind === "start") void send(noCriteria ? { confirm_scoring_change: true } : {});
    else void send({ ...pending.flags, confirm_scoring_change: true });
  };

  const dialog =
    pending?.kind === "scoring"
      ? {
          title: "채점 방식이 바뀝니다",
          body: `${SCORING_CHANGE_TEXT} 이 TC는 지금 👎별 기준을 합쳐 채점하는데, 새 기준을 만들면 앞으로 이 기준으로만 채점하고 참조 답은 쓰지 않습니다. 계속할까요?`,
          ok: "계속",
        }
      : {
          title: "기준 다시 만들기",
          body: `이 답의 ${count === undefined ? "받아 둔 👎" : `👎 ${count}건`}(사유·코멘트)을 모두 넣어 새 판단 기준을 만듭니다. 코멘트가 길면 AI로 요약해서 넣습니다. 옛 기준과 옛 점수는 그대로 남습니다.${noCriteria ? ` ${SCORING_CHANGE_TEXT} 앞으로 이 기준으로만 채점하고 참조 답은 쓰지 않습니다.` : ""}`,
          ok: "만들기",
        };

  return (
    <div className="grid gap-2 rounded-lg border border-border border-l-2 border-l-primary bg-surface-subtle p-3">
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] tracking-widest text-muted-foreground uppercase">확정 채점 기준</span>
        {typeof detail.criteria_version === "number" && (
          <Badge size="compact">기준 v{detail.criteria_version}</Badge>
        )}
      </span>
      {checks && checks.length > 0 ? (
        <CheckList checks={checks} />
      ) : (
        <p className="text-[13px] text-muted-foreground">확정된 채점 기준이 없습니다.</p>
      )}

      {notice && <Banner tone="success">{notice}</Banner>}
      {failure && (
        <Banner tone="error">
          {failure} {KEEP_CRITERIA_TEXT}
        </Banner>
      )}
      {rereadFailed && (
        <Banner tone="warn">
          새 기준은 저장됐지만 화면을 다시 읽지 못했습니다.{" "}
          <Button size="sm" variant="outline" disabled={checking}
                  onClick={async () => setRereadFailed(!(await reread()))}>
            다시 읽기
          </Button>
        </Banner>
      )}
      {unknown && (
        <Banner tone="warn">
          요청 결과를 확인하지 못했습니다. 새 기준이 저장됐을 수도 있으니 아래 기준 버전으로 확인해 주세요.
          {unknown.operationId ? ` (요청 번호 ${unknown.operationId})` : ""}{" "}
          <Button size="sm" variant="outline" disabled={checking} onClick={() => void reread()}>
            결과 다시 확인
          </Button>
        </Banner>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setPending({ kind: "start" })}
          disabled={running || !allowed}
          title={gate && !gate.allowed ? gate.reason : undefined}
        >
          {running && <Loader2 className="animate-spin" />}
          {running ? "기준 만드는 중" : "기준 다시 만들기"}
        </Button>
        {gate && !gate.allowed && (
          <p className="basis-full text-[12px] text-muted-foreground">{gate.reason}</p>
        )}
      </div>

      {typeof detail.criteria_version === "number" && (
        <CriteriaHistory
          // Remount after a new version so the open list is fetched again.
          key={detail.criteria_version}
          load={() => source.criteriaVersions(detail.test_case_id, detail.source_environment)}
        />
      )}

      <Dialog open={pending !== null} onOpenChange={(open: boolean) => { if (!open) setPending(null); }}>
        <DialogContent showCloseButton={false}>
          <DialogTitle>{dialog.title}</DialogTitle>
          <DialogDescription>{dialog.body}</DialogDescription>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPending(null)}>
              취소
            </Button>
            <Button onClick={accept}>{dialog.ok}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CheckList({ checks }: { checks: NonNullable<TestCaseCriteria["checks"]> }) {
  return (
    <ul className="grid gap-0.5 text-[14px]">
      {checks.map((check, index) => (
        <li key={index}>
          {check.title ?? check.criterion} · 가중치 {check.score_impact}
        </li>
      ))}
    </ul>
  );
}

const ORIGIN_TEXT: Record<string, string> = {
  initial: "처음 기준",
  manual_regeneration: "다시 만든 기준",
};

/** Optional (B8): fetched only when opened. */
function CriteriaHistory({ load }: { load: () => Promise<CriteriaVersionList> }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<CriteriaVersionList | null>(null);
  const [error, setError] = useState("");

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && list === null) {
      setError("");
      load()
        .then(setList)
        .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    }
  };

  return (
    <div className="grid gap-2">
      <button
        type="button"
        className="w-fit cursor-pointer text-left text-[12px] text-link hover:underline"
        aria-expanded={open}
        onClick={toggle}
      >
        {open ? "기준 버전 기록 접기" : "기준 버전 기록 보기"}
      </button>
      {open &&
        (error ? (
          <p className="text-[12px] text-muted-foreground">기록을 불러오지 못했습니다 · {error}</p>
        ) : list === null ? (
          <Skeleton className="h-10" />
        ) : list.items.length === 0 ? (
          <p className="text-[12px] text-muted-foreground">남은 기록이 없습니다.</p>
        ) : (
          <ul className="grid gap-2">
            {list.items.map((item) => (
              <HistoryItem key={item.version} item={item} current={item.version === list.current_version} />
            ))}
          </ul>
        ))}
    </div>
  );
}

function HistoryItem({ item, current }: { item: CriteriaVersion; current: boolean }) {
  const checks = item.criteria?.checks ?? [];
  return (
    <li className="grid gap-1 rounded-md border border-border bg-background px-3 py-2">
      <span className="flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
        <Badge size="compact" variant={current ? "accent" : "default"}>
          v{item.version}
          {current ? " · 지금 쓰는 기준" : ""}
        </Badge>
        <span>{ORIGIN_TEXT[item.origin] ?? "기준"}</span>
        <span className="tabular-nums">· {compactTime(item.created_at)}</span>
        {item.created_by && <span className="break-all">· {item.created_by}</span>}
      </span>
      <span className="text-[12px] text-muted-foreground">
        {item.feedback_count === null ? "" : `👎 ${item.feedback_count}건으로 만듦`}
        {commentModeText(item.comment_mode) ? ` · ${commentModeText(item.comment_mode)}` : ""}
      </span>
      {checks.length > 0 ? (
        <CheckList checks={checks} />
      ) : (
        <span className="text-[12px] text-muted-foreground">항목 없음</span>
      )}
    </li>
  );
}
